import type { ContentfulAssetFragment, Maybe } from "@server/gql/graphql";
import type { ComponentPropsWithoutRef } from "react";

import { breakpoints } from "@/config/breakpoints";
import { cn } from "@/lib/utils";
import { combineSourceMedia } from "./sourceMedia";

type ContentfulResponsiveVideoProps = Omit<
  ComponentPropsWithoutRef<"video">,
  "src" | "children"
> & {
  desktopVideo?: Maybe<ContentfulAssetFragment>;
  mobileVideo?: Maybe<ContentfulAssetFragment>;
  mobileBreakpoint?: (typeof breakpoints)[keyof typeof breakpoints];
  sourceMedia?: string;
  when?: string;
};

export default function ContentfulResponsiveVideo({
  className,
  desktopVideo,
  mobileVideo,
  mobileBreakpoint = breakpoints.lg,
  sourceMedia,
  when,
  autoPlay = true,
  ...props
}: ContentfulResponsiveVideoProps) {
  const video = desktopVideo?.url ? desktopVideo : mobileVideo;

  if (!video?.url) {
    return null;
  }

  const mobileSource =
    mobileVideo?.url && mobileVideo.url !== video.url ? mobileVideo : undefined;
  const constrainedSource = sourceMedia && !mobileSource ? video : undefined;

  return (
    <video
      className={cn("object-cover", className)}
      autoPlay={autoPlay}
      playsInline
      loop
      muted
      {...props}
    >
      {/* shortcut: the browser picks a source once at load, use ContentfulVideo when the file must swap as the viewport crosses the breakpoint */}
      {constrainedSource?.url ? (
        <source
          media={combineSourceMedia(sourceMedia, when)}
          src={constrainedSource.url}
          type={constrainedSource.contentType || undefined}
        />
      ) : (
        <>
          {mobileSource?.url ? (
            <source
              media={combineSourceMedia(
                `(max-width: ${mobileBreakpoint}px)`,
                when,
              )}
              src={mobileSource.url}
              type={mobileSource.contentType || undefined}
            />
          ) : null}
          <source
            media={combineSourceMedia(undefined, when)}
            src={video.url}
            type={video.contentType || undefined}
          />
        </>
      )}
    </video>
  );
}
